import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { API_BASE_URL } from "../../../config";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EmployeesAppraisalList = () => {
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // 🔹 Filters
  const [year, setYear] = useState(new Date().getFullYear());
  const [department, setDepartment] = useState("ALL");

  /* ===============================
     FETCH DATA
  ================================*/
  useEffect(() => {
    setLoading(true);

    axios
      .get(`${API_BASE_URL}/employees-appraisal/list`, {
        params: { year },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        const data = res.data.data || [];
        setRows(data);
        setFilteredRows(data);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load appraisal list");
        setLoading(false);
      });
  }, [year]);

  /* ===============================
     DEPARTMENT OPTIONS
  ================================*/
  const departmentOptions = useMemo(() => {
    return [
      "ALL",
      ...Array.from(
        new Set(rows.map((r) => r.department_name).filter(Boolean))
      ),
    ];
  }, [rows]);

  /* ===============================
     FILTER LOGIC
  ================================*/
  useEffect(() => {
    let result = rows;

    // Department filter
    if (department !== "ALL") {
      result = result.filter(
        (r) => r.department_name === department
      );
    }

    // Search filter
    if (search) {
      result = result.filter((r) =>
        `${r.employee_id} ${r.employee_name} ${r.department_name}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    setFilteredRows(result);
  }, [rows, department, search]);

  /* ===============================
     TABLE COLUMNS
  ================================*/
  const columns = [
    { name: "Emp ID", selector: (r) => r.employee_id, sortable: true },
    { name: "Name", selector: (r) => r.employee_name, sortable: true },
    { name: "Department", selector: (r) => r.department_name, sortable: true },
    { name: "Month", selector: (r) => r.req_date },

    ...["self", "incharge", "hod", "hr", "management", "others"].map((key) => ({
      name: key.toUpperCase(),
      cell: (row) =>
        row[key] ? (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() =>
              history.push(`/EmployeeScoreView/${row[key].summary_id}`)
            }
          >
            {row[key].total_score}
          </button>
        ) : (
          "--"
        ),
    })),

    {
      name: "Total",
      cell: (row) => {
        const scores = ["self", "incharge", "hod", "hr", "management", "others"]
          .map((k) => (row[k] ? parseInt(row[k].total_score, 10) : null))
          .filter((v) => !isNaN(v) && v !== null);

        const total = scores.reduce((a, b) => a + b, 0);
        return scores.length > 0 ? total : "--";
      },
    },
    {
      name: "Average",
      cell: (row) => {
        const scores = ["self", "incharge", "hod", "hr", "management", "others"]
          .map((k) => (row[k] ? parseInt(row[k].total_score, 10) : null))
          .filter((v) => !isNaN(v) && v !== null);

        const total = scores.reduce((a, b) => a + b, 0);
        return scores.length > 0 ? Math.round(total / scores.length) : "--";
      },
    },

    {
      name: "Report",
      cell: (row) => (
        <button
          className="btn btn-sm btn-outline-success"
          onClick={() =>
            history.push(`/EmployeeOverallReport/${row.request_id}`)
          }
        >
          Report
        </button>
      ),
    },
  ];

  return (
    <div className="card">
      <ToastContainer position="bottom-right" />

      {/* ================= HEADER ================= */}
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <i className="fa fa-star mr-2" style={{ fontSize: "20px" }} />
            All Employees Appraisal Score List
          </h3>
          <small className="text-muted">Threshold Score : 50</small>
        </div>

        <div className="d-flex gap-2">
          {/* Year */}
          <select
            className="form-control form-control-sm"
            style={{ width: 120 }}
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {[2022, 2023, 2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {/* Department */}
          <select
            className="form-control form-control-sm"
            style={{ width: 180 }}
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            {departmentOptions.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>

          {/* Search */}
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search employee / dept"
            style={{ width: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card-body">
        <DataTable
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          highlightOnHover
          striped
          dense
          responsive
        />
      </div>
    </div>
  );
};

export default EmployeesAppraisalList;
